import React, { useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button, TextField } from '@mui/material';
import { ChevronLeft } from 'lucide-react';
import axiosInstance from '../api/axiosInstance';

export default function Settings({
  isOwner,
  employeeBusinesses = [],
  onClose,
  navigate,
  username,
  userId,
  isBlocked,
  setIsBlocked,
  setAccessToken,
}) {
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleBlockUser = async () => {
    try {
      await axiosInstance.post('/block/block', { blockedId: userId });
      alert(`You have blocked user ${username}.`);
      setIsBlocked(true);
    } catch (error) {
      console.error('Error blocking user:', error);
      alert('Failed to block user.');
    }
  };

  const handleUnblockUser = async () => {
    try {
      await axiosInstance.post('/block/unblock', { blockedId: userId });
      alert(`You have unblocked user ${username}.`);
      setIsBlocked(false);
    } catch (error) {
      console.error('Error unblocking user:', error);
      alert('Failed to unblock user.');
    }
  };

  const handleShareProfile = () => {
    const shareUrl = `${window.location.origin}/profile/${userId}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      alert('Profile link copied to clipboard!');
    });
  };

  const handleDeleteAccount = async () => {
    setError('');
    if (!password) {
      setError('Password is required.');
      return;
    }
    try {
      const res = await axiosInstance.delete(`/profile/delete`, {
        data: { userId, password },
      });
      if (res.status === 200) {
        localStorage.removeItem('accessToken');
        setAccessToken(null);
        navigate('/login');
      }
    } catch (err) {
      console.error('Error deleting account:', err);
      setError(err.response?.data?.message || 'Failed to delete account.');
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-white dark:bg-black z-[1000] flex flex-col mb-16">
        <div className="top-header">
          <button className="back-button" onClick={onClose}>
            <ChevronLeft className="w-8 h-8" />
          </button>
          <h1 className="top-title">Settings</h1>
        </div>

        <div className="tailwind flex-1 overflow-y-auto px-6 py-4">
          <ul className="list-none space-y-2">
            {isOwner ? (
              <>
                <h2 className="first text-lg font-semibold mt-6 mb-2 text-gray-800 dark:text-white">Account Settings</h2>
                <li>
                  <button className="w-full text-base flex items-center justify-between px-4 py-3 rounded dark:text-gray-300" onClick={() => navigate('/chat/conversations')}>
                    Chat
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </li>
                <li>
                  <button className="w-full text-base flex items-center justify-between px-4 py-3 rounded dark:text-gray-300" onClick={() => navigate('/profile-upload')}>
                    Edit Profile Picture
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </li>
                <li>
                  <button className="w-full text-base flex items-center justify-between px-4 py-3 rounded dark:text-gray-300" onClick={() => navigate('/manage-sessions')}>
                    Manage Devices
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </li>
                <li>
                  <button className="w-full text-base flex items-center justify-between px-4 py-3 rounded dark:text-gray-300" onClick={() => navigate('/change-password')}>
                    Change Password
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </li>
                <li>
                  <button className="w-full text-base flex items-center justify-between px-4 py-3 rounded dark:text-gray-300" onClick={() => navigate('/edit-close-friends')}>
                    Edit Close Friends
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </li>
                <li>
                  <button
                    className="w-full text-base flex items-center justify-between px-4 py-3 rounded dark:text-gray-300"
                    onClick={() => navigate('/settings/appearance')}
                  >
                    Appearance
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </li>
                <li>
                  <button className="w-full text-base flex items-center justify-between px-4 py-3 rounded dark:text-gray-300" onClick={() => navigate('/mytabs')}>
                    My trippins
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </li>
                <li>
                  <button className="w-full text-base flex items-center justify-between px-4 py-3 rounded dark:text-gray-300" onClick={() => navigate('/tabs')}>
                    Post a trippin
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </li>
                <li>
                  <button className="w-full text-base flex items-center justify-between px-4 py-3 rounded dark:text-gray-300" onClick={() => navigate('/business')}>
                    Business Management
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </li>

                <h2 className="text-lg font-semibold mt-[1.5rem_!important] mb-2 text-gray-800 dark:text-white">Legal</h2>
                <li>
                  <button
                    className="w-full text-base flex items-center justify-between px-4 py-3 rounded dark:text-gray-300"
                    onClick={() => navigate('/privacy-policy')}
                  >
                    Privacy Policy
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </li>
                <li>
                  <button
                    className="w-full text-base flex items-center justify-between px-4 py-3 rounded dark:text-gray-300"
                    onClick={() => navigate('/terms-conditions')}
                  >
                    Terms & Conditions
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </li>

                <h2 className="text-lg font-semibold mt-[1.5rem_!important] mb-2 text-gray-800 dark:text-white">Reset Options</h2>
                <li>
                  <button
                    className="w-full text-base flex items-center justify-between px-4 py-3 rounded text-likeColor"
                    onClick={async () => {
                      try {
                        await axiosInstance.post('/auth/logout');
                      } catch (err) {
                        console.error('Logout failed', err);
                      }
                      setAccessToken(null);
                      navigate('/login');
                    }}
                  >
                    Logout
                  </button>
                </li>
                <li>
                  <button
                    className="w-full text-base flex items-center justify-between px-4 py-3 rounded text-likeColor"
                    onClick={() => {
                      setOpenDeleteDialog(true);
                    }}
                  >
                    Delete Account
                  </button>
                </li>
              </>
            ) : (
              <>
                <li>
                  {isBlocked ? (
                    <button className="w-full text-base flex items-center justify-between px-4 py-3 rounded dark:text-gray-300" onClick={handleUnblockUser}>
                      Unblock User
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  ) : (
                    <button className="w-full text-base flex items-center justify-between px-4 py-3 rounded dark:text-gray-300" onClick={handleBlockUser}>
                      Block User
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  )}
                </li>
                <li>
                  <button className="w-full text-base flex items-center justify-between px-4 py-3 rounded dark:text-gray-300" onClick={handleShareProfile}>
                    Share Profile
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </li>
              </>
            )}
          </ul>
        </div>
      </div>

      {/* DELETE ACCOUNT CONFIRMATION DIALOG */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete your account? This action is irreversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>
            Cancel
          </Button>
          <Button color="error" onClick={() => { setOpenDeleteDialog(false); setOpenPasswordDialog(true); }}>
            Proceed
          </Button>
        </DialogActions>
      </Dialog>

      {/* PASSWORD CONFIRMATION DIALOG */}
      <Dialog
        open={openPasswordDialog}
        onClose={() => setOpenPasswordDialog(false)}
      >
        <DialogTitle>Confirm Password</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please enter your password to confirm account deletion.
          </DialogContentText>
          <TextField
            fullWidth
            type="password"
            label="Password"
            variant="outlined"
            margin="dense"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!error}
            helperText={error}
          />
        </DialogContent>
        <DialogActions>
          <Button sx={{ textTransform: "none" }} onClick={() => setOpenPasswordDialog(false)}>
            Cancel
          </Button>
          <Button sx={{ textTransform: "none" }} onClick={handleDeleteAccount} color="error">
            Delete Account
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}